import axios from 'axios';
import { useEffect } from 'react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/** @internal */
function isDuckType(value, quack) {
    return typeof value == 'object' && value != null && quack in value;
}

var LoggedInState;
(function (LoggedInState) {
    LoggedInState["LOGGED_IN"] = "LOGGED_IN";
    LoggedInState["NOT_LOGGED_IN"] = "NOT_LOGGED_IN";
})(LoggedInState || (LoggedInState = {}));
/**
 * Utility class for wrapping API code with refreh token logic.
 **/
class RefreshTokenUtil {
    constructor(endpoint) {
        this.endpoint = endpoint;
        this.pending = false;
        this.scheduled = [];
        this.subscriptions = new Set();
    }
    /**
     * Wraps an execution function with refresh token logic.
     * @param run The execution function.
     * @param [options] The options to customise the refresh token logic.
     **/
    wrap(run, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.handle(run, options);
            }
            catch (error) {
                if (this.isTokenExpired(error)) {
                    return yield this.refresh(run);
                }
                throw error;
            }
        });
    }
    handle(run, { doLoginStateCheck = false }) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield run();
            if (!doLoginStateCheck)
                return response;
            if (this.isLoggedOut(response)) {
                try {
                    return yield this.refresh(run);
                }
                catch (_a) { }
            }
            return response;
        });
    }
    isLoggedOut(result) {
        if (isDuckType(result, 'data')) {
            return this.isLoggedOut(result.data);
        }
        if (isDuckType(result, 'state')) {
            return result.state == 'NOT_LOGGED_IN';
        }
        return false;
    }
    isTokenExpired(error) {
        var _a;
        return ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 401;
    }
    refresh(run) {
        return __awaiter(this, void 0, void 0, function* () {
            const promise = this.schedule(run);
            if (this.pending)
                return promise;
            this.pending = true;
            let rejection;
            try {
                yield this.request();
            }
            catch (error) {
                rejection = error;
            }
            this.pending = false;
            this.publish(rejection);
            this.cleanup();
            return promise;
        });
    }
    schedule(run) {
        return new Promise((resolve, reject) => {
            this.scheduled.push(error => {
                if (error)
                    return reject(error);
                resolve(run());
            });
        });
    }
    publish(error) {
        const execute = (subscriber) => subscriber(error);
        this.subscriptions.forEach(execute);
        this.scheduled.forEach(execute);
    }
    cleanup() {
        this.scheduled = [];
    }
    /**
     * Sends a request to refresh the token.
     **/
    request() {
        if (!this.endpoint)
            throw new ReferenceError('Refresh token endpoint is not set properly.');
        return axios.post(this.endpoint);
    }
    unsubscibe(subscriber) {
        this.subscriptions.delete(subscriber);
        process.env.NODE_ENV;
    }
    /**
     * A subscriber function called after refresh attempt of expired token.
     **/
    subscribe(subscriber) {
        this.subscriptions.add(subscriber);
        return () => this.unsubscibe(subscriber);
    }
}

/**
 * @example
 * useRefreshTokenSubscriber((error) => {
 *   if (error) {
 *     dispach({ type: 'REFRESH_TOKEN_ERROR', payload: error });
 *   } else {
 *     console.log('Refresh token has been refreshed');
 *   }
 * }, refreshTokenUtil);
 **/
function useRefreshTokenSubscriber(callback, refreshTokenUtil) {
    useEffect(() => refreshTokenUtil.subscribe(callback), [refreshTokenUtil]);
}

export { LoggedInState, RefreshTokenUtil, useRefreshTokenSubscriber };
//# sourceMappingURL=module.js.map
